import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { AutoAwesome, Close, CloudUpload, Computer, ExpandMore, Settings } from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import type {
  BackgroundAiPreset,
  BackgroundAiTag,
  BackgroundAiTheme,
  BackgroundAiUsageGeneration,
  BackgroundAiUsageMachine,
  MachineInfo,
} from '../../services/api';
import { useI18n } from '../../i18n/useI18n';
import type { AppLang } from '../../i18n';
import { rewriteToCdn } from '../../utils/cdn';
import { formatAvailabilityStamp, isInsideSchedule, toLocalInput } from '../Theme/ThemeAvailabilityDialog';

type NameLabels = { vi: string; en: string; ko: string; zh: string };
const emptyNameLabels = (): NameLabels => ({ vi: '', en: '', ko: '', zh: '' });
const NAME_FIELD_ORDER = ['vi', 'zh', 'en', 'ko'] as const;
const NAME_FIELD_LABEL: Record<(typeof NAME_FIELD_ORDER)[number], string> = {
  vi: 'Tiếng Việt',
  en: 'English',
  ko: '한국어',
  zh: '中文',
};

function labelsFromItem(
  item: { name?: string; nameLabels?: Partial<NameLabels> },
  lang: AppLang,
): NameLabels {
  const labels: NameLabels = {
    vi: item.nameLabels?.vi || '',
    en: item.nameLabels?.en || '',
    ko: item.nameLabels?.ko || '',
    zh: item.nameLabels?.zh || '',
  };
  if (!labels.vi && !labels.en && !labels.ko && !labels.zh) labels[lang] = item.name || '';
  return labels;
}

function displayName(item: { name: string; nameLabels?: Partial<NameLabels> }, lang: AppLang) {
  return item.nameLabels?.[lang] || item.name;
}

function usageName(labels: Partial<NameLabels> | undefined, fallback: string | undefined, lang: AppLang) {
  return String(labels?.[lang] || '').trim() || String(fallback || '').trim();
}

type ThemeDraft = {
  name: string;
  nameLabels: NameLabels;
  image?: File;
  imageUrl?: string;
  scheduleEnabled: boolean;
  availableFrom: string;
  availableUntil: string;
};
type PresetDraft = {
  name: string;
  nameLabels: NameLabels;
  prompt: string;
  tags: string[];
  image?: File;
  imageUrl?: string;
  referenceImage?: File;
  referenceImageUrl?: string;
  clearReference?: boolean;
};

type Props = {
  adminUserId?: string;
  username?: string;
  onActiveThemeChange?: (theme: BackgroundAiTheme | null) => void;
};

const emptyTheme = (): ThemeDraft => ({
  name: '',
  nameLabels: emptyNameLabels(),
  scheduleEnabled: false,
  availableFrom: '',
  availableUntil: '',
});

type MachineDraft = {
  proEnabled: boolean;
  liteEnabled: boolean;
  proMachineCodes: string[];
  liteMachineCodes: string[];
};
const emptyPreset = (): PresetDraft => ({ name: '', nameLabels: emptyNameLabels(), prompt: '', tags: [] });

const imageDropSx = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.75,
  minHeight: 188,
  p: 2,
  textAlign: 'center',
  cursor: 'pointer',
  borderRadius: 2.5,
  border: '1.5px dashed',
  borderColor: alpha('#ff8ba7', 0.7),
  bgcolor: '#fff',
  overflow: 'hidden',
  '&:hover': { borderColor: '#f48fb1', bgcolor: '#fff8fb' },
} as const;

function ImageDropZone({
  file,
  existingUrl,
  inputRef,
  label,
  hint,
  onFile,
}: {
  file?: File;
  existingUrl?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  label: string;
  hint: string;
  onFile: (file: File) => void;
}) {
  const [preview, setPreview] = useState('');

  useEffect(() => {
    if (!file) {
      setPreview(existingUrl ? rewriteToCdn(existingUrl) : '');
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file, existingUrl]);

  return (
    <Paper
      elevation={0}
      component="label"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        const next = event.dataTransfer.files?.[0];
        if (next) onFile(next);
      }}
      sx={imageDropSx}
    >
      {preview ? (
        <Box
          component="img"
          src={preview}
          alt=""
          sx={{ width: '100%', height: 160, objectFit: 'contain', borderRadius: 2, bgcolor: '#fff0f5' }}
        />
      ) : (
        <CloudUpload sx={{ fontSize: 42, color: '#f48fb1' }} />
      )}
      <Typography variant="body2" fontWeight={800} color="#bf5f82">
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {file ? file.name : hint}
      </Typography>
      <input
        ref={inputRef}
        hidden
        type="file"
        accept="image/png,image/jpeg"
        onChange={(event) => {
          const next = event.target.files?.[0];
          if (next) onFile(next);
          event.target.value = '';
        }}
      />
    </Paper>
  );
}

const TAG_PALETTE = ['#5b8def', '#ec628a', '#7e57c2', '#26a69a', '#fb8c00', '#43a047', '#8e24aa', '#039be5'];

const tagColor = (key: string) => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return TAG_PALETTE[hash % TAG_PALETTE.length];
};

const tagChipSx = (key: string, selected = true) => {
  const color = tagColor(key);
  return {
    bgcolor: selected ? color : 'transparent',
    color: selected ? '#fff' : color,
    border: '1px solid',
    borderColor: color,
    fontWeight: 700,
    '&:hover': { bgcolor: selected ? color : 'transparent' },
  };
};

const themeCardSx = {
  borderRadius: 3,
  overflow: 'hidden',
  border: '1px solid transparent',
  boxShadow: '0 10px 24px rgba(191, 95, 130, 0.12)',
  transition: 'transform 0.16s ease, box-shadow 0.16s ease',
  '&&': {
    backgroundColor: 'transparent',
    backgroundImage:
      'linear-gradient(165deg, #ffffff 0%, #fff8fb 58%, #fff0f5 100%), linear-gradient(135deg, #ffc1d4, #ff8ba7 55%, #f48fb1)',
    backgroundOrigin: 'padding-box, border-box',
    backgroundClip: 'padding-box, border-box',
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 16px 32px rgba(191, 95, 130, 0.2)',
  },
};

const themeMediaSx = {
  objectFit: 'contain' as const,
  bgcolor: '#fff0f5',
  borderBottom: '1px solid rgba(255, 139, 167, 0.35)',
};

const productChipSx = (on: boolean) => ({
  height: 22,
  fontWeight: 800,
  fontSize: '0.68rem',
  bgcolor: on ? alpha('#ff8ba7', 0.2) : alpha('#ff8ba7', 0.06),
  color: on ? '#bf5f82' : alpha('#bf5f82', 0.45),
  border: '1px solid',
  borderColor: on ? alpha('#ff8ba7', 0.7) : alpha('#ff8ba7', 0.28),
});

const cardCloseBtnSx = {
  position: 'absolute',
  top: 6,
  right: 6,
  zIndex: 1,
  width: 30,
  height: 30,
  bgcolor: '#e53935',
  color: '#fff',
  boxShadow: '0 2px 8px rgba(183, 28, 28, 0.35)',
  '&:hover': { bgcolor: '#c62828', color: '#fff' },
};

const themeIconBtnSx = {
  color: '#e06a88',
  bgcolor: alpha('#ff8ba7', 0.12),
  p: 0.5,
  '&:hover': { bgcolor: alpha('#ff8ba7', 0.28) },
};

const cardGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr' },
  gap: { xs: 1, sm: 1.5, md: 2 },
};

const pinkFill = 'linear-gradient(135deg, #ffc1d4 0%, #ff8ba7 52%, #f48fb1 100%)';
const pinkHover = 'linear-gradient(135deg, #ff8ba7 0%, #f48fb1 52%, #bf5f82 100%)';

const formDialogPaperSx = {
  m: { xs: 1.5, sm: 4 },
  width: { xs: 'calc(100% - 24px)', sm: '100%' },
  maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
  borderRadius: 3,
  overflow: 'hidden',
  boxShadow: '0 24px 60px rgba(191, 95, 130, 0.18)',
  '&&': {
    border: '1px solid transparent',
    backgroundColor: 'transparent',
    backgroundImage:
      'linear-gradient(165deg, #ffffff 0%, #fff8fb 72%, #fff5f8 100%), linear-gradient(135deg, #ffc1d4, #ff8ba7 55%, #f48fb1)',
    backgroundOrigin: 'padding-box, border-box',
    backgroundClip: 'padding-box, border-box',
  },
};

const formDialogTitleSx = {
  fontWeight: 800,
  color: '#bf5f82',
  background: 'linear-gradient(135deg, #fff5f8 0%, #ffeef3 100%)',
  borderBottom: '1px solid',
  borderColor: alpha('#ff8ba7', 0.35),
};

const formDialogActionsSx = {
  px: 2,
  py: 1.5,
  gap: 1,
  borderTop: '1px solid',
  borderColor: alpha('#ff8ba7', 0.28),
  background: 'linear-gradient(180deg, #ffffff 0%, #fff8fb 100%)',
};

const formFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: '#fff',
    '& fieldset': { borderColor: alpha('#ff8ba7', 0.55) },
    '&:hover fieldset': { borderColor: '#ff8ba7' },
    '&.Mui-focused fieldset': { borderColor: '#f48fb1' },
  },
};

const pinkOutlineBtnSx = {
  color: '#bf5f82',
  fontWeight: 700,
  borderRadius: 999,
  textTransform: 'none' as const,
  border: '1px solid transparent',
  backgroundColor: 'transparent',
  backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(135deg, #ffc1d4, #ff8ba7, #f48fb1)',
  backgroundOrigin: 'padding-box, border-box',
  backgroundClip: 'padding-box, border-box',
};

const pinkSaveBtnSx = {
  fontWeight: 800,
  borderRadius: 999,
  textTransform: 'none' as const,
  color: '#fff',
  background: pinkFill,
  boxShadow: '0 8px 16px rgba(191, 95, 130, 0.28)',
  '&:hover': { background: pinkHover },
  '&.Mui-disabled': { color: '#fff', background: alpha('#ff8ba7', 0.45), boxShadow: 'none' },
};

const pinkSwitchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundImage: 'linear-gradient(90deg, #ffc1d4, #ff8ba7)',
    opacity: 1,
  },
};

const builtinTagLabels: Record<string, { vi: string; en: string; ko: string; zh: string }> = {
  male: { vi: 'Nam', en: 'Male', ko: '남성', zh: '남' },
  female: { vi: 'Nữ', en: 'Female', ko: '여성', zh: '여' },
  group: { vi: 'Nhóm', en: 'Group', ko: '그룹', zh: '단체' },
};

const tagLabelForLang = (
  key: string,
  catalog: BackgroundAiTag[],
  lang: AppLang,
) => {
  const found = catalog.find((tag) => tag.key === key);
  const labels = found?.labels || builtinTagLabels[key];
  if (!labels) return key;
  return labels[lang] || labels.vi || labels.en || labels.ko || labels.zh || key;
};

const PresetTags: React.FC<{ tags?: string[]; catalog: BackgroundAiTag[]; lang: AppLang }> = ({ tags, catalog, lang }) => {
  const items = tags || [];
  if (!items.length) return null;
  const labelOf = (key: string) => tagLabelForLang(key, catalog, lang);
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
      {items.map((tag) => (
        <Chip key={tag} size="small" label={labelOf(tag)} sx={tagChipSx(tag)} />
      ))}
    </Stack>
  );
};

const machineSide = (machine: MachineInfo) => {
  const product = String(machine.appProduct || '').toLowerCase();
  const platform = String(machine.appPlatform || '').toLowerCase();
  if (product === 'lite' || platform === 'android') return 'lite';
  if (product === 'pro' || platform === 'windows' || !product) return 'pro';
  return '';
};

const MachinePicker: React.FC<{
  title: string;
  hint: string;
  empty: string;
  note: string;
  selectAllLabel: string;
  clearAllLabel: string;
  enabled: boolean;
  codes: string[];
  machines: MachineInfo[];
  loading?: boolean;
  onEnabled: (value: boolean) => void;
  onToggle: (code: string) => void;
  onSetCodes: (codes: string[]) => void;
}> = ({ title, hint, empty, note, selectAllLabel, clearAllLabel, enabled, codes, machines, loading, onEnabled, onToggle, onSetCodes }) => (
  <Box>
    <FormControlLabel
      control={<Switch checked={enabled} onChange={(event) => onEnabled(event.target.checked)} sx={pinkSwitchSx} />}
      label={<Typography fontWeight={800} sx={{ color: '#bf5f82' }}>{title}</Typography>}
    />
    <Typography variant="caption" color="text.secondary" display="block">{hint}</Typography>
    {enabled ? (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
          <Button
            size="small"
            disabled={machines.length === 0}
            onClick={() => onSetCodes(machines.map((machine) => machine.machineCode.toUpperCase()))}
            sx={{ color: '#bf5f82', fontWeight: 700, textTransform: 'none' }}
          >
            {selectAllLabel}
          </Button>
          <Button
            size="small"
            disabled={machines.length === 0 || codes.length === 0}
            onClick={() => onSetCodes([])}
            sx={{ color: '#bf5f82', fontWeight: 700, textTransform: 'none' }}
          >
            {clearAllLabel}
          </Button>
        </Box>
        <Box
          sx={{
            mt: 1,
            maxHeight: 168,
            overflow: 'auto',
            borderRadius: 2,
            px: 1,
            border: '1px solid transparent',
            backgroundColor: 'transparent',
            backgroundImage: 'linear-gradient(#fff, #fff8fb), linear-gradient(135deg, #ffc1d4, #ff8ba7)',
            backgroundOrigin: 'padding-box, border-box',
            backgroundClip: 'padding-box, border-box',
          }}
        >
          {loading ? (
            <Box display="flex" alignItems="center" gap={1} sx={{ py: 1 }}>
              <CircularProgress size={16} sx={{ color: '#ff8ba7' }} />
            </Box>
          ) : machines.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>{empty}</Typography>
          ) : machines.map((machine) => {
            const code = machine.machineCode.toUpperCase();
            return (
              <FormControlLabel
                key={code}
                sx={{ display: 'flex', mr: 0 }}
                control={<Checkbox size="small" checked={codes.includes(code)} onChange={() => onToggle(code)} sx={{ color: '#f48fb1', '&.Mui-checked': { color: '#ff8ba7' } }} />}
                label={`${machine.machineName || code} (${code})`}
              />
            );
          })}
        </Box>
        <Typography variant="caption" color="text.secondary">{note}</Typography>
      </>
    ) : null}
  </Box>
);

const UserAiThemes: React.FC<Props> = ({ adminUserId, username, onActiveThemeChange }) => {
  const location = useLocation();
  const { pages: p, admin, formatMessage, lang } = useI18n();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const t = adminUserId ? null : p.aiCreate;
  const b = admin.backgroundAi;
  const copy = {
    title: t?.title || b.themeDialog,
    subtitle: t?.subtitle || (username ? username : ''),
    disabled: t?.disabled || '',
    createTheme: t?.createTheme || b.addTheme,
    viewUsage: t?.viewUsage || b.usageTitle,
    usageTheme: t?.usageTheme || b.usageTheme,
    usageBackground: t?.usageBackground || b.usageBackground,
    editTheme: t?.editTheme || b.editTheme,
    themeName: t?.themeName || b.themeName,
    description: t?.description || b.themeDescription,
    themeImage: t?.themeImage || b.themeImage,
    noThemes: t?.noThemes || b.noUserThemes,
    backgrounds: t?.backgrounds || b.backgrounds,
    backgroundCount: t?.backgroundCount || b.backgroundCount,
    emptyBackgrounds: t?.emptyBackgrounds || b.emptyBackgrounds,
    createBackground: t?.createBackground || b.addBackground,
    editBackground: t?.editBackground || b.editBackground,
    backgroundName: t?.backgroundName || b.backgroundName,
    prompt: t?.prompt || b.prompt,
    tags: t?.tags || b.tags,
    tagMale: t?.tagMale || b.tagMale,
    tagFemale: t?.tagFemale || b.tagFemale,
    tagGroup: t?.tagGroup || b.tagGroup,
    tagsRequired: t?.tagsRequired || b.tagsRequired,
    image: t?.image || b.chooseImage,
    referenceImage: t?.referenceImage || 'Ảnh tham khảo (tuỳ chọn)',
    referenceHint: t?.referenceHint || 'Ảnh khách chụp là ảnh 1. Ảnh này là ảnh 2 để AI tham khảo.',
    removeReference: t?.removeReference || 'Bỏ ảnh tham khảo',
    proEnable: t?.proEnable || 'Máy Pro',
    proHint: t?.proHint || 'App Pro và mobile upload mở từ máy Pro.',
    liteEnable: t?.liteEnable || 'Máy Lite',
    liteHint: t?.liteHint || 'App Android và mobile upload mở từ máy Lite.',
    machineHint: t?.machineHint || 'Chỉ máy được tích mới thấy. Không tích máy nào thì không hiện.',
    noMachines: t?.noMachines || 'Chưa có máy loại này.',
    back: t?.back || b.back,
    save: t?.save || b.save,
    cancel: t?.cancel || b.cancel,
    delete: t?.delete || p.common.delete,
    confirmDeleteTheme: t?.confirmDeleteTheme || b.confirmDeleteTheme,
    confirmDeleteBackground: t?.confirmDeleteBackground || b.confirmDeleteBackground,
  };

  const [enabled, setEnabled] = useState(Boolean(adminUserId));
  const [loading, setLoading] = useState(true);
  const [themes, setThemes] = useState<BackgroundAiTheme[]>([]);
  const [tagCatalog, setTagCatalog] = useState<BackgroundAiTag[]>([]);
  const [tagDialog, setTagDialog] = useState(false);
  const [tagDraft, setTagDraft] = useState({ vi: '', en: '', ko: '', zh: '' });
  const [translating, setTranslating] = useState(false);
  const [activeTheme, setActiveTheme] = useState<BackgroundAiTheme | null>(null);
  const [themeDialog, setThemeDialog] = useState(false);
  const [editingTheme, setEditingTheme] = useState<BackgroundAiTheme | null>(null);
  const [themeDraft, setThemeDraft] = useState<ThemeDraft>(emptyTheme());
  const [presetDialog, setPresetDialog] = useState(false);
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [editingPreset, setEditingPreset] = useState<BackgroundAiPreset | null>(null);
  const [presetDraft, setPresetDraft] = useState<PresetDraft>(emptyPreset());
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState<null | { kind: 'theme' | 'preset' | 'tag'; id: string; name: string }>(null);
  const themeFileRef = useRef<HTMLInputElement | null>(null);
  const presetFileRef = useRef<HTMLInputElement | null>(null);
  const referenceFileRef = useRef<HTMLInputElement | null>(null);
  const [machines, setMachines] = useState<MachineInfo[]>([]);
  const [loadingMachines, setLoadingMachines] = useState(false);
  const [machineTheme, setMachineTheme] = useState<BackgroundAiTheme | null>(null);
  const [machineDraft, setMachineDraft] = useState<MachineDraft | null>(null);
  const [usageOpen, setUsageOpen] = useState(false);
  const [usageMachines, setUsageMachines] = useState<BackgroundAiUsageMachine[]>([]);
  const [usageQuota, setUsageQuota] = useState<{ used: number; remaining: number | null; limit: number | null } | null>(null);
  const [usagePages, setUsagePages] = useState<Record<string, { items: BackgroundAiUsageGeneration[]; loading: boolean }>>({});
  const [openUsageCode, setOpenUsageCode] = useState<string | null>(null);
  const [usageSearch, setUsageSearch] = useState('');
  const [usageProduct, setUsageProduct] = useState<'all' | 'lite' | 'pro'>('all');
  const usageFiltersRef = useRef<HTMLDivElement | null>(null);
  const [usageFiltersHeight, setUsageFiltersHeight] = useState(0);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const usageRequestRef = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (!adminUserId) {
        const status = await api.getBackgroundAiStatus();
        setEnabled(Boolean(status.enabled));
        if (!status.enabled) {
          setThemes([]);
          return;
        }
      }
      const [result, tagResult] = await Promise.all([
        adminUserId
          ? api.getAdminUserBackgroundAiThemes(adminUserId)
          : api.getMyBackgroundAiThemes(),
        adminUserId
          ? api.getAdminUserBackgroundAiTags(adminUserId)
          : api.getMyBackgroundAiTags(),
      ]);
      setThemes(result.themes || []);
      setTagCatalog(tagResult.tags || []);
      setActiveTheme((current) => {
        if (!current) return null;
        return (result.themes || []).find((theme) => theme.id === current.id) || null;
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Không tải được chủ đề AI.');
    } finally {
      setLoading(false);
    }
  }, [adminUserId]);

  const qualityLabel = (quality: string) => {
    if (quality === 'medium') return b.qualityMedium;
    if (quality === 'high' || quality === 'xhigh' || quality === 'max') return b.qualityHigh;
    return b.qualityLow;
  };

  const closeUsage = () => {
    usageRequestRef.current += 1;
    setUsageOpen(false);
    setOpenUsageCode(null);
    setUsagePages({});
    setUsageSearch('');
    setUsageProduct('all');
  };

  const openUsage = async () => {
    const ticket = usageRequestRef.current + 1;
    usageRequestRef.current = ticket;
    setUsageOpen(true);
    setUsageQuota(null);
    setUsageMachines([]);
    setOpenUsageCode(null);
    setUsagePages({});
    setUsageSearch('');
    setUsageProduct('all');
    setLoadingUsage(true);
    try {
      const result = await api.getMyBackgroundAiUsage();
      if (ticket !== usageRequestRef.current) return;
      setUsageMachines(result.machines || []);
      setUsageQuota({
        used: result.quotaUsed || 0,
        remaining: result.quotaRemaining ?? null,
        limit: result.quotaLimit ?? null,
      });
    } catch (error: any) {
      if (ticket !== usageRequestRef.current) return;
      toast.error(error?.response?.data?.error || b.usageLoadFail);
    } finally {
      if (ticket === usageRequestRef.current) setLoadingUsage(false);
    }
  };

  const loadUsagePage = async (machineCode: string, skip: number) => {
    const ticket = usageRequestRef.current;
    setUsagePages((prev) => ({
      ...prev,
      [machineCode]: { items: skip > 0 ? prev[machineCode]?.items || [] : [], loading: true },
    }));
    try {
      const result = await api.getMyBackgroundAiUsagePage(machineCode, skip);
      if (ticket !== usageRequestRef.current) return;
      setUsagePages((prev) => ({
        ...prev,
        [machineCode]: {
          items: [...(skip > 0 ? prev[machineCode]?.items || [] : []), ...(result.generations || [])],
          loading: false,
        },
      }));
    } catch (error: any) {
      if (ticket !== usageRequestRef.current) return;
      setUsagePages((prev) => ({
        ...prev,
        [machineCode]: { items: prev[machineCode]?.items || [], loading: false },
      }));
      toast.error(error?.response?.data?.error || b.usageLoadFail);
    }
  };

  const visibleUsageMachines = usageMachines.filter((machine) => {
    const product = String(machine.appProduct || '').trim().toLowerCase();
    if (usageProduct !== 'all' && product !== usageProduct) return false;
    const query = usageSearch.trim().toLowerCase();
    if (!query) return true;
    const name = String(machine.machineName || '').toLowerCase();
    const code = String(machine.machineCode || '').toLowerCase();
    return name.includes(query) || code.includes(query);
  });

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (adminUserId) return;
    let cancelled = false;
    void api.getMyBackgroundAiUsage()
      .then((result) => {
        if (cancelled) return;
        setUsageQuota({
          used: result.quotaUsed || 0,
          remaining: result.quotaRemaining ?? null,
          limit: result.quotaLimit ?? null,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [adminUserId]);

  const machineFilterOpen = Boolean(machineTheme);
  useEffect(() => {
    if (!machineFilterOpen) return;
    let cancelled = false;
    setLoadingMachines(true);
    const request = adminUserId ? api.getAdminUserMachines(adminUserId) : api.getUserMachines();
    request
      .then((result) => {
        if (!cancelled) setMachines(result.machines || []);
      })
      .catch(() => {
        if (!cancelled) setMachines([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingMachines(false);
      });
    return () => {
      cancelled = true;
    };
  }, [machineFilterOpen, adminUserId]);

  useEffect(() => {
    onActiveThemeChange?.(activeTheme);
  }, [activeTheme, onActiveThemeChange]);

  useEffect(() => {
    if (adminUserId) return;
    if (location.pathname !== '/ai-themes') setActiveTheme(null);
  }, [adminUserId, location.pathname]);

  useEffect(() => {
    const onBack = (event: Event) => {
      if (!activeTheme) return;
      if (!adminUserId) event.preventDefault();
      setActiveTheme(null);
    };
    window.addEventListener('ai-themes-back', onBack);
    return () => window.removeEventListener('ai-themes-back', onBack);
  }, [adminUserId, activeTheme]);

  useEffect(() => {
    const el = usageFiltersRef.current;
    if (!el) {
      setUsageFiltersHeight(0);
      return;
    }
    const update = () => setUsageFiltersHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [usageOpen, loadingUsage, usageMachines.length]);

  const openCreateTheme = () => {
    setEditingTheme(null);
    setThemeDraft(emptyTheme());
    setThemeDialog(true);
  };

  const openEditTheme = (theme: BackgroundAiTheme, event?: React.MouseEvent) => {
    event?.stopPropagation();
    setEditingTheme(theme);
    const nameLabels = labelsFromItem(theme, lang);
    setThemeDraft({
      name: nameLabels[lang] || theme.name,
      nameLabels,
      imageUrl: theme.imageUrl || '',
      scheduleEnabled: theme.scheduleEnabled === true,
      availableFrom: toLocalInput(theme.availableFrom),
      availableUntil: toLocalInput(theme.availableUntil),
    });
    setThemeDialog(true);
  };

  const openMachineFilter = (theme: BackgroundAiTheme, event?: React.MouseEvent) => {
    event?.stopPropagation();
    setMachineTheme(theme);
    setMachineDraft({
      proEnabled: theme.proEnabled !== false,
      liteEnabled: theme.liteEnabled !== false,
      proMachineCodes: (theme.proMachineCodes || []).map((code) => code.toUpperCase()),
      liteMachineCodes: (theme.liteMachineCodes || []).map((code) => code.toUpperCase()),
    });
  };

  const availabilityCaption = (theme: BackgroundAiTheme) => {
    const timeLabel = theme.scheduleEnabled
      ? isInsideSchedule(theme)
        ? `${formatAvailabilityStamp(theme.availableFrom)} – ${formatAvailabilityStamp(theme.availableUntil)}`
        : p.themes.scheduleOutside
      : p.themes.scheduleAlways;
    const count = (theme.proMachineCodes?.length || 0) + (theme.liteMachineCodes?.length || 0);
    const machineLabel = count
      ? p.themes.scheduleMachineCount.replace('{n}', String(count))
      : p.themes.scheduleNoMachines;
    return `${timeLabel} · ${machineLabel}`;
  };

  const saveTheme = async () => {
    const name = (themeDraft.nameLabels[lang] || themeDraft.name).trim();
    if (!name) return;
    if (themeDraft.scheduleEnabled) {
      if (!themeDraft.availableFrom || !themeDraft.availableUntil) {
        toast.error('Cần chọn thời gian bắt đầu và kết thúc');
        return;
      }
      if (new Date(themeDraft.availableUntil) <= new Date(themeDraft.availableFrom)) {
        toast.error('Thời gian kết thúc phải sau thời gian bắt đầu');
        return;
      }
    }
    const payload = {
      ...themeDraft,
      name,
      sourceLang: lang,
      nameLabels: { ...themeDraft.nameLabels, [lang]: name },
      scheduleEnabled: themeDraft.scheduleEnabled,
      availableFrom: themeDraft.availableFrom ? new Date(themeDraft.availableFrom).toISOString() : null,
      availableUntil: themeDraft.availableUntil ? new Date(themeDraft.availableUntil).toISOString() : null,
    };
    setSaving(true);
    try {
      if (adminUserId) {
        if (editingTheme) {
          await api.updateAdminUserBackgroundAiTheme(adminUserId, editingTheme.id, payload);
        } else {
          await api.createAdminUserBackgroundAiTheme(adminUserId, payload);
        }
      } else if (editingTheme) {
        await api.updateMyBackgroundAiTheme(editingTheme.id, payload);
      } else {
        await api.createMyBackgroundAiTheme(payload);
      }
      setThemeDialog(false);
      await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Không lưu được chủ đề.');
    } finally {
      setSaving(false);
    }
  };

  const saveMachineFilter = async () => {
    if (!machineTheme || !machineDraft) return;
    const nameLabels = labelsFromItem(machineTheme, lang);
    const name = (nameLabels[lang] || machineTheme.name).trim();
    setSaving(true);
    try {
      const payload = {
        name,
        sourceLang: lang,
        nameLabels: { ...nameLabels, [lang]: name },
        ...machineDraft,
      };
      if (adminUserId) {
        await api.updateAdminUserBackgroundAiTheme(adminUserId, machineTheme.id, payload);
      } else {
        await api.updateMyBackgroundAiTheme(machineTheme.id, payload);
      }
      setMachineTheme(null);
      setMachineDraft(null);
      await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || p.themes.machineFilterSaveFail);
    } finally {
      setSaving(false);
    }
  };

  const openCreatePreset = () => {
    setEditingPreset(null);
    setPresetDraft(emptyPreset());
    setPromptExpanded(false);
    setPresetDialog(true);
  };

  const openEditPreset = (preset: BackgroundAiPreset) => {
    setEditingPreset(preset);
    const nameLabels = labelsFromItem(preset, lang);
    setPresetDraft({
      name: nameLabels[lang] || preset.name,
      nameLabels,
      prompt: preset.prompt || '',
      tags: preset.tags || [],
      imageUrl: preset.imageUrl || '',
      referenceImageUrl: preset.referenceImageUrl || '',
    });
    setPromptExpanded(false);
    setPresetDialog(true);
  };

  const savePreset = async () => {
    const name = (presetDraft.nameLabels[lang] || presetDraft.name).trim();
    if (!activeTheme || !name || !presetDraft.prompt.trim()) return;
    const payload = {
      ...presetDraft,
      name,
      sourceLang: lang,
      nameLabels: { ...presetDraft.nameLabels, [lang]: name },
    };
    setSaving(true);
    try {
      if (adminUserId) {
        if (editingPreset) {
          await api.updateAdminUserBackgroundAiPreset(adminUserId, activeTheme.id, editingPreset.id, payload);
        } else {
          await api.createAdminUserBackgroundAiPreset(adminUserId, activeTheme.id, payload);
        }
      } else if (editingPreset) {
        await api.updateMyBackgroundAiPreset(activeTheme.id, editingPreset.id, payload);
      } else {
        await api.createMyBackgroundAiPreset(activeTheme.id, payload);
      }
      setPresetDialog(false);
      await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Không lưu được nền AI.');
    } finally {
      setSaving(false);
    }
  };

  const runDelete = async () => {
    if (!confirm) return;
    setSaving(true);
    try {
      if (confirm.kind === 'theme') {
        if (adminUserId) await api.deleteAdminUserBackgroundAiTheme(adminUserId, confirm.id);
        else await api.deleteMyBackgroundAiTheme(confirm.id);
        if (activeTheme?.id === confirm.id) setActiveTheme(null);
      } else if (confirm.kind === 'tag') {
        const result = await api.deleteBackgroundAiTag(confirm.id, adminUserId);
        setTagCatalog(result.tags || []);
        setPresetDraft((draft) => ({
          ...draft,
          tags: draft.tags.filter((item) => item !== confirm.id),
        }));
      } else if (activeTheme) {
        if (adminUserId) await api.deleteAdminUserBackgroundAiPreset(adminUserId, activeTheme.id, confirm.id);
        else await api.deleteMyBackgroundAiPreset(activeTheme.id, confirm.id);
      }
      setConfirm(null);
      await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Không xóa được.');
    } finally {
      setSaving(false);
    }
  };

  const body = !enabled && !loading ? (
    <Alert severity="info">{copy.disabled}</Alert>
  ) : loading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
      <CircularProgress />
    </Box>
  ) : activeTheme ? (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} noWrap>
            {displayName(activeTheme, lang)}
          </Typography>
          {activeTheme.description ? (
            <Typography variant="body2" color="text.secondary" noWrap>
              {activeTheme.description}
            </Typography>
          ) : null}
          <Typography
            variant="caption"
            display="block"
            color={activeTheme.scheduleEnabled && !isInsideSchedule(activeTheme) ? 'warning.main' : 'text.secondary'}
          >
            {availabilityCaption(activeTheme)}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={!isMobile ? <Computer sx={{ fontSize: 18 }} /> : undefined}
          onClick={() => openMachineFilter(activeTheme)}
          aria-label={p.themes.machineFilterButton}
          sx={{
            flexShrink: 0,
            textTransform: 'none',
            fontWeight: 800,
            borderRadius: 999,
            color: '#bf5f82',
            borderColor: alpha('#ff8ba7', 0.7),
            ...(isMobile ? { minWidth: 40, width: 40, height: 40, p: 0 } : { px: 2 }),
          }}
        >
          {isMobile ? <Computer sx={{ fontSize: 18 }} /> : p.themes.machineFilterButton}
        </Button>
        <Button
          variant="contained"
          onClick={openCreatePreset}
          sx={{
            flexShrink: 0,
            color: '#fff',
            background: 'linear-gradient(135deg, #ffc1d4 0%, #ff8ba7 52%, #f48fb1 100%)',
            boxShadow: '0 8px 16px rgba(191, 95, 130, 0.28)',
            '&:hover': { background: 'linear-gradient(135deg, #ff8ba7 0%, #f48fb1 52%, #bf5f82 100%)' },
          }}
        >
          {copy.createBackground}
        </Button>
      </Stack>
      {(activeTheme.presets || []).length === 0 ? (
        <Alert severity="info">{copy.emptyBackgrounds}</Alert>
      ) : (
        <Box sx={cardGridSx}>
          {(activeTheme.presets || []).map((preset) => (
            <Card
              key={preset.id}
              elevation={0}
              onClick={() => openEditPreset(preset)}
              sx={{ ...themeCardSx, cursor: 'pointer', position: 'relative' }}
            >
              <IconButton
                size="small"
                aria-label={copy.delete}
                onClick={(event) => {
                  event.stopPropagation();
                  setConfirm({ kind: 'preset', id: preset.id, name: displayName(preset, lang) });
                }}
                sx={cardCloseBtnSx}
              >
                <Close sx={{ fontSize: 20 }} />
              </IconButton>
              {preset.imageUrl ? (
                <CardMedia component="img" image={rewriteToCdn(preset.imageUrl)} alt={displayName(preset, lang)} sx={{ ...themeMediaSx, height: { xs: 112, sm: 150 } }} />
              ) : (
                <Box sx={{ height: { xs: 112, sm: 150 }, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff0f5', borderBottom: '1px solid rgba(255, 139, 167, 0.35)' }}>
                  <AutoAwesome sx={{ color: '#ff8ba7' }} />
                </Box>
              )}
              <CardContent sx={{ py: { xs: 1, sm: 1.25 }, px: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } } }}>
                <Typography fontWeight={800} noWrap sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>{displayName(preset, lang)}</Typography>
                <PresetTags tags={preset.tags} catalog={tagCatalog} lang={lang} />
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  ) : (
    <Box>
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 2 }}>
        {!adminUserId && (
          <Button
            variant="outlined"
            onClick={() => void openUsage()}
            sx={{ color: '#ff8ba7', borderColor: '#ff8ba7', fontWeight: 700 }}
          >
            {copy.viewUsage}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={openCreateTheme}
          sx={{
            color: '#fff',
            background: 'linear-gradient(135deg, #ffc1d4 0%, #ff8ba7 52%, #f48fb1 100%)',
            boxShadow: '0 8px 16px rgba(191, 95, 130, 0.28)',
            '&:hover': { background: 'linear-gradient(135deg, #ff8ba7 0%, #f48fb1 52%, #bf5f82 100%)' },
          }}
        >
          {copy.createTheme}
        </Button>
      </Stack>
      {themes.length === 0 ? (
        <Alert severity="info">{copy.noThemes}</Alert>
      ) : (
        <Box sx={cardGridSx}>
          {themes.map((theme) => (
            <Card
              key={theme.id}
              elevation={0}
              onClick={() => setActiveTheme(theme)}
              sx={{ ...themeCardSx, cursor: 'pointer', position: 'relative' }}
            >
              <IconButton
                size="small"
                aria-label={copy.delete}
                onClick={(event) => {
                  event.stopPropagation();
                  setConfirm({ kind: 'theme', id: theme.id, name: displayName(theme, lang) });
                }}
                sx={cardCloseBtnSx}
              >
                <Close sx={{ fontSize: 20 }} />
              </IconButton>
              {theme.imageUrl ? (
                <CardMedia component="img" image={rewriteToCdn(theme.imageUrl)} alt={displayName(theme, lang)} sx={{ ...themeMediaSx, height: { xs: 112, sm: 168 } }} />
              ) : (
                <Box sx={{ height: { xs: 112, sm: 168 }, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff0f5', borderBottom: '1px solid rgba(255, 139, 167, 0.35)' }}>
                  <AutoAwesome sx={{ fontSize: 40, color: '#ff8ba7' }} />
                </Box>
              )}
              <CardContent sx={{ py: { xs: 1, sm: 1.35 }, px: { xs: 1, sm: 1.5 }, '&:last-child': { pb: { xs: 1, sm: 1.35 } } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
                  <Typography fontWeight={800} noWrap sx={{ minWidth: 0, flex: 1, fontSize: { xs: '0.85rem', sm: '1rem' } }}>{displayName(theme, lang)}</Typography>
                  <IconButton
                    size="small"
                    aria-label={p.themes.scheduleOpen}
                    onClick={(event) => openEditTheme(theme, event)}
                    sx={{ ...themeIconBtnSx, flexShrink: 0 }}
                  >
                    <Settings sx={{ fontSize: 18 }} />
                  </IconButton>
                </Stack>
                <Typography variant="caption" sx={{ color: '#e06a88', display: 'block' }} noWrap>
                  {theme.description || formatMessage(copy.backgroundCount, { n: (theme.presets || []).length })}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label="Pro" sx={productChipSx(theme.proEnabled !== false)} />
                  <Chip size="small" label="Lite" sx={productChipSx(theme.liteEnabled !== false)} />
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ py: adminUserId ? 0 : 3 }} disableGutters={Boolean(adminUserId)}>
      {!adminUserId && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, minWidth: 0 }}>
          <Typography variant="h5" fontWeight={800} noWrap sx={{ minWidth: 0, flex: '0 1 auto' }}>{copy.title}</Typography>
          {usageQuota && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Chip
                label={formatMessage(b.quotaUsed, { n: usageQuota.used })}
                sx={{ height: 28, fontWeight: 800, color: '#bf5f82', bgcolor: alpha('#ff8ba7', 0.16), '& .MuiChip-label': { px: 1.25 } }}
              />
              <Chip
                label={usageQuota.limit == null ? b.quotaUnlimited : formatMessage(b.quotaRemaining, { n: usageQuota.remaining || 0 })}
                sx={{ height: 28, fontWeight: 800, color: '#bf5f82', bgcolor: alpha('#ff8ba7', 0.16), '& .MuiChip-label': { px: 1.25 } }}
              />
            </Stack>
          )}
        </Stack>
      )}
      {body}

      <Dialog
        open={themeDialog}
        onClose={(_, reason) => {
          if (saving) return;
          if (reason === 'backdropClick' && document.activeElement === themeFileRef.current) return;
          setThemeDialog(false);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: formDialogPaperSx }}
      >
        <DialogTitle sx={formDialogTitleSx}>{editingTheme ? p.themes.scheduleOpen : copy.createTheme}</DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden', pt: '20px !important' }}>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label={copy.themeName}
              value={themeDraft.nameLabels[lang]}
              onChange={(e) => {
                const value = e.target.value;
                setThemeDraft((d) => ({ ...d, name: value, nameLabels: { ...d.nameLabels, [lang]: value } }));
              }}
              fullWidth
              sx={formFieldSx}
            />
            <Button
              disabled={translating || saving || !themeDraft.nameLabels[lang].trim()}
              onClick={() => {
                const text = themeDraft.nameLabels[lang].trim();
                setTranslating(true);
                void api.translateBackgroundAiName(lang, text).then((result) => {
                  setThemeDraft((d) => ({ ...d, name: text, nameLabels: { ...result.labels, [lang]: text } }));
                }).catch((error: any) => {
                  toast.error(error?.response?.data?.error || 'Không dịch được.');
                }).finally(() => setTranslating(false));
              }}
              sx={pinkOutlineBtnSx}
            >
              {translating ? '...' : ({ vi: 'Dịch tự động', ko: '자동 번역', en: 'Translate', zh: '自动翻译' } as const)[lang]}
            </Button>
            {NAME_FIELD_ORDER.filter((code) => code !== lang).map((code) => (
              <TextField
                key={code}
                label={NAME_FIELD_LABEL[code]}
                value={themeDraft.nameLabels[code]}
                onChange={(e) => {
                  const value = e.target.value;
                  setThemeDraft((d) => ({ ...d, nameLabels: { ...d.nameLabels, [code]: value } }));
                }}
                fullWidth
                sx={formFieldSx}
              />
            ))}
            <ImageDropZone
              file={themeDraft.image}
              existingUrl={themeDraft.imageUrl}
              inputRef={themeFileRef}
              label={p.themes.dropImage}
              hint={p.themes.imageFormats}
              onFile={(file) => setThemeDraft((draft) => ({ ...draft, image: file }))}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={themeDraft.scheduleEnabled}
                  onChange={(_, checked) => setThemeDraft((draft) => ({ ...draft, scheduleEnabled: checked }))}
                  sx={pinkSwitchSx}
                />
              }
              label={<Typography fontWeight={800} sx={{ color: '#bf5f82' }}>{p.themes.scheduleEnable}</Typography>}
            />
            <Typography variant="caption" color="text.secondary" display="block">{p.themes.scheduleHint}</Typography>
            <Collapse in={themeDraft.scheduleEnabled} unmountOnExit>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <TextField
                  label={p.themes.scheduleFrom}
                  type="datetime-local"
                  value={themeDraft.availableFrom}
                  onChange={(event) => setThemeDraft((draft) => ({ ...draft, availableFrom: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={formFieldSx}
                />
                <TextField
                  label={p.themes.scheduleUntil}
                  type="datetime-local"
                  value={themeDraft.availableUntil}
                  onChange={(event) => setThemeDraft((draft) => ({ ...draft, availableUntil: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={formFieldSx}
                />
              </Box>
            </Collapse>
          </Stack>
        </DialogContent>
        <DialogActions sx={formDialogActionsSx}>
          <Button onClick={() => setThemeDialog(false)} disabled={saving} sx={{ color: '#e06a88', fontWeight: 700, textTransform: 'none' }}>{copy.cancel}</Button>
          <Button onClick={() => void saveTheme()} disabled={saving || !themeDraft.name.trim()} sx={pinkSaveBtnSx}>{copy.save}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(machineTheme && machineDraft)}
        onClose={saving ? undefined : () => { setMachineTheme(null); setMachineDraft(null); }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: formDialogPaperSx }}
      >
        <DialogTitle sx={formDialogTitleSx}>{p.themes.machineFilterButton}</DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden', pt: '16px !important' }}>
          {machineDraft && (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <MachinePicker
                title={copy.proEnable}
                hint={copy.proHint}
                empty={copy.noMachines}
                note={copy.machineHint}
                selectAllLabel={p.themes.machineFilterSelectAll}
                clearAllLabel={p.themes.machineFilterClearAll}
                enabled={machineDraft.proEnabled}
                codes={machineDraft.proMachineCodes}
                loading={loadingMachines}
                machines={machines.filter((machine) => machineSide(machine) === 'pro')}
                onEnabled={(proEnabled) => setMachineDraft((draft) => (draft ? { ...draft, proEnabled } : draft))}
                onToggle={(code) => setMachineDraft((draft) => (draft ? {
                  ...draft,
                  proMachineCodes: draft.proMachineCodes.includes(code)
                    ? draft.proMachineCodes.filter((item) => item !== code)
                    : [...draft.proMachineCodes, code],
                } : draft))}
                onSetCodes={(proMachineCodes) => setMachineDraft((draft) => (draft ? { ...draft, proMachineCodes } : draft))}
              />
              <MachinePicker
                title={copy.liteEnable}
                hint={copy.liteHint}
                empty={copy.noMachines}
                note={copy.machineHint}
                selectAllLabel={p.themes.machineFilterSelectAll}
                clearAllLabel={p.themes.machineFilterClearAll}
                enabled={machineDraft.liteEnabled}
                codes={machineDraft.liteMachineCodes}
                loading={loadingMachines}
                machines={machines.filter((machine) => machineSide(machine) === 'lite')}
                onEnabled={(liteEnabled) => setMachineDraft((draft) => (draft ? { ...draft, liteEnabled } : draft))}
                onToggle={(code) => setMachineDraft((draft) => (draft ? {
                  ...draft,
                  liteMachineCodes: draft.liteMachineCodes.includes(code)
                    ? draft.liteMachineCodes.filter((item) => item !== code)
                    : [...draft.liteMachineCodes, code],
                } : draft))}
                onSetCodes={(liteMachineCodes) => setMachineDraft((draft) => (draft ? { ...draft, liteMachineCodes } : draft))}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={formDialogActionsSx}>
          <Button
            onClick={() => { setMachineTheme(null); setMachineDraft(null); }}
            disabled={saving}
            sx={{ color: '#e06a88', fontWeight: 700, textTransform: 'none' }}
          >
            {copy.cancel}
          </Button>
          <Button onClick={() => void saveMachineFilter()} disabled={saving || !machineDraft} sx={pinkSaveBtnSx}>{copy.save}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={presetDialog}
        onClose={(_, reason) => {
          if (saving) return;
          if (reason === 'backdropClick' && (document.activeElement === presetFileRef.current || document.activeElement === referenceFileRef.current)) return;
          setPresetDialog(false);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: formDialogPaperSx }}
      >
        <DialogTitle sx={formDialogTitleSx}>{editingPreset ? copy.editBackground : copy.createBackground}</DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden' }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={copy.backgroundName}
              value={presetDraft.nameLabels[lang]}
              onChange={(e) => {
                const value = e.target.value;
                setPresetDraft((d) => ({ ...d, name: value, nameLabels: { ...d.nameLabels, [lang]: value } }));
              }}
              fullWidth
              sx={formFieldSx}
            />
            <Button
              disabled={translating || saving || !presetDraft.nameLabels[lang].trim()}
              onClick={() => {
                const text = presetDraft.nameLabels[lang].trim();
                setTranslating(true);
                void api.translateBackgroundAiName(lang, text).then((result) => {
                  setPresetDraft((d) => ({ ...d, name: text, nameLabels: { ...result.labels, [lang]: text } }));
                }).catch((error: any) => {
                  toast.error(error?.response?.data?.error || 'Không dịch được.');
                }).finally(() => setTranslating(false));
              }}
              sx={pinkOutlineBtnSx}
            >
              {translating ? '...' : ({ vi: 'Dịch tự động', ko: '자동 번역', en: 'Translate', zh: '自动翻译' } as const)[lang]}
            </Button>
            {NAME_FIELD_ORDER.filter((code) => code !== lang).map((code) => (
              <TextField
                key={code}
                label={NAME_FIELD_LABEL[code]}
                value={presetDraft.nameLabels[code]}
                onChange={(e) => {
                  const value = e.target.value;
                  setPresetDraft((d) => ({ ...d, nameLabels: { ...d.nameLabels, [code]: value } }));
                }}
                fullWidth
                sx={formFieldSx}
              />
            ))}
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2">{copy.prompt}</Typography>
                <Button
                  size="small"
                  onClick={() => setPromptExpanded((open) => !open)}
                  sx={{ color: '#bf5f82', fontWeight: 700, textTransform: 'none', minWidth: 0, py: 0 }}
                >
                  {promptExpanded
                    ? ({ vi: 'Thu gọn', ko: '접기', en: 'Collapse', zh: '收起' } as const)[lang]
                    : ({ vi: 'Mở rộng', ko: '펼치기', en: 'Expand', zh: '展开' } as const)[lang]}
                </Button>
              </Stack>
              <TextField
                value={presetDraft.prompt}
                onChange={(e) => setPresetDraft((d) => ({ ...d, prompt: e.target.value }))}
                fullWidth
                multiline
                minRows={promptExpanded ? 12 : 3}
                maxRows={promptExpanded ? 16 : 3}
                sx={formFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.75 }}>{copy.tags}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {tagCatalog.map((tag) => {
                  const selected = presetDraft.tags.includes(tag.key);
                  const label = tagLabelForLang(tag.key, tagCatalog, lang);
                  return (
                    <Chip
                      key={tag.key}
                      label={label}
                      clickable
                      variant="outlined"
                      sx={tagChipSx(tag.key, selected)}
                      onDelete={tag.custom ? (event) => {
                        event.stopPropagation();
                        setConfirm({ kind: 'tag', id: tag.key, name: label });
                      } : undefined}
                      onClick={() => setPresetDraft((draft) => ({
                        ...draft,
                        tags: selected ? draft.tags.filter((item) => item !== tag.key) : [...draft.tags, tag.key],
                      }))}
                    />
                  );
                })}
                {tagCatalog.length < 5 ? (
                  <Chip
                    label={({ vi: 'Thêm tag', ko: '태그 추가', en: 'Add tag', zh: '添加标签' } as const)[lang]}
                    clickable
                    variant="outlined"
                    onClick={() => {
                      setTagDraft({ vi: '', en: '', ko: '', zh: '' });
                      setTagDialog(true);
                    }}
                    sx={{ fontWeight: 700, borderColor: '#f48fb1', color: '#bf5f82' }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {({ vi: 'Tối đa 5 tag.', ko: '태그는 최대 5개입니다.', en: 'Up to 5 tags.', zh: '最多 5 个标签。' } as const)[lang]}
                  </Typography>
                )}
              </Stack>
            </Box>
            <ImageDropZone
              file={presetDraft.image}
              existingUrl={presetDraft.imageUrl}
              inputRef={presetFileRef}
              label={copy.image}
              hint={p.themes.imageFormats}
              onFile={(file) => setPresetDraft((draft) => ({ ...draft, image: file }))}
            />
            <ImageDropZone
              file={presetDraft.referenceImage}
              existingUrl={presetDraft.clearReference ? '' : presetDraft.referenceImageUrl}
              inputRef={referenceFileRef}
              label={copy.referenceImage}
              hint={copy.referenceHint}
              onFile={(file) => setPresetDraft((draft) => ({ ...draft, referenceImage: file, clearReference: false }))}
            />
            {(presetDraft.referenceImage || (presetDraft.referenceImageUrl && !presetDraft.clearReference)) ? (
              <Button
                color="inherit"
                onClick={() => setPresetDraft((draft) => ({
                  ...draft,
                  referenceImage: undefined,
                  clearReference: true,
                  referenceImageUrl: '',
                }))}
              >
                {copy.removeReference}
              </Button>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={formDialogActionsSx}>
          <Button onClick={() => setPresetDialog(false)} disabled={saving} sx={{ color: '#e06a88', fontWeight: 700, textTransform: 'none' }}>{copy.cancel}</Button>
          <Button
            onClick={() => void savePreset()}
            disabled={saving || !presetDraft.name.trim() || !presetDraft.prompt.trim()}
            sx={pinkSaveBtnSx}
          >
            {copy.save}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tagDialog} onClose={() => !translating && !saving && setTagDialog(false)} fullWidth maxWidth="xs" PaperProps={{ sx: formDialogPaperSx }}>
        <DialogTitle sx={formDialogTitleSx}>{({ vi: 'Thêm tag', ko: '태그 추가', en: 'Add tag', zh: '添加标签' } as const)[lang]}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {({ vi: 'Tag này chỉ lưu cho tài khoản này.', ko: '이 태그는 이 계정에만 저장됩니다.', en: 'This tag is saved for this account only.', zh: '此标签只保存在这个账户。' } as const)[lang]}
            </Typography>
            <TextField label="Tiếng Việt" value={tagDraft.vi} onChange={(e) => setTagDraft((d) => ({ ...d, vi: e.target.value }))} fullWidth sx={formFieldSx} />
            <Button
              disabled={translating || !tagDraft.vi.trim()}
              onClick={() => {
                setTranslating(true);
                void api.translateBackgroundAiTag(tagDraft.vi.trim()).then((result) => {
                  setTagDraft(result.labels);
                }).catch((error: any) => {
                  toast.error(error?.response?.data?.error || 'Không dịch được.');
                }).finally(() => setTranslating(false));
              }}
              sx={pinkOutlineBtnSx}
            >
              {translating ? '...' : ({ vi: 'Dịch tự động', ko: '자동 번역', en: 'Translate', zh: '自动翻译' } as const)[lang]}
            </Button>
            <TextField label="English" value={tagDraft.en} onChange={(e) => setTagDraft((d) => ({ ...d, en: e.target.value }))} fullWidth sx={formFieldSx} />
            <TextField label="한국어" value={tagDraft.ko} onChange={(e) => setTagDraft((d) => ({ ...d, ko: e.target.value }))} fullWidth sx={formFieldSx} />
            <TextField label="中文" value={tagDraft.zh} onChange={(e) => setTagDraft((d) => ({ ...d, zh: e.target.value }))} fullWidth sx={formFieldSx} />
          </Stack>
        </DialogContent>
        <DialogActions sx={formDialogActionsSx}>
          <Button onClick={() => setTagDialog(false)} disabled={saving} sx={{ color: '#e06a88', fontWeight: 700, textTransform: 'none' }}>{copy.cancel}</Button>
          <Button
            disabled={saving || !tagDraft.vi.trim()}
            sx={pinkSaveBtnSx}
            onClick={() => {
              setSaving(true);
              void api.createBackgroundAiTag({
                vi: tagDraft.vi.trim(),
                en: tagDraft.en.trim(),
                ko: tagDraft.ko.trim(),
                zh: tagDraft.zh.trim(),
              }, adminUserId).then((result) => {
                setTagCatalog(result.tags || []);
                setTagDialog(false);
              }).catch((error: any) => {
                toast.error(error?.response?.data?.error || 'Không lưu được tag.');
              }).finally(() => setSaving(false));
            }}
          >
            {copy.save}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={usageOpen}
        onClose={closeUsage}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            m: { xs: 1.5, sm: 4 },
            width: { xs: 'calc(100% - 24px)', sm: '100%' },
            maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
            overflow: 'hidden',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#bf5f82', pb: 1 }}>{copy.viewUsage}</DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden' }}>
          {!loadingUsage && usageMachines.length > 0 && (
            <Stack
              ref={usageFiltersRef}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 3,
                bgcolor: '#fff',
                pb: 1.25,
              }}
            >
              <TextField
                size="small"
                fullWidth
                placeholder={b.usageSearch}
                value={usageSearch}
                onChange={(e) => setUsageSearch(e.target.value)}
                sx={{
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': { height: 40, borderRadius: 2 },
                }}
              />
              <ToggleButtonGroup
                exclusive
                value={usageProduct}
                onChange={(_, value: 'all' | 'lite' | 'pro' | null) => {
                  if (value) setUsageProduct(value);
                }}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  flexShrink: 0,
                  height: 40,
                  '& .MuiToggleButton-root': {
                    flex: { xs: 1, sm: 'none' },
                    height: 40,
                    px: { xs: 0.5, sm: 1.75 },
                    minWidth: { xs: 0, sm: 76 },
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                    fontWeight: 700,
                    lineHeight: 1,
                    borderColor: alpha('#ff8ba7', 0.35),
                  },
                  '& .Mui-selected': {
                    color: '#bf5f82 !important',
                    bgcolor: `${alpha('#ff8ba7', 0.16)} !important`,
                  },
                }}
              >
                <ToggleButton value="all">{b.usageFilterAll}</ToggleButton>
                <ToggleButton value="lite">Lite</ToggleButton>
                <ToggleButton value="pro">Pro</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          )}
          {loadingUsage ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : usageMachines.length === 0 ? (
            <Alert severity="info">{b.usageEmpty}</Alert>
          ) : visibleUsageMachines.length === 0 ? (
            <Alert severity="info">{b.usageNoMatch}</Alert>
          ) : (
            <Stack spacing={1}>
              {visibleUsageMachines.map((machine) => {
                const open = openUsageCode === machine.machineCode;
                const page = usagePages[machine.machineCode];
                const items = page?.items || [];
                const product = String(machine.appProduct || '').trim().toLowerCase();
                return (
                  <Accordion
                    key={machine.machineCode}
                    expanded={open}
                    onChange={(_, isExpanded) => {
                      if (!isExpanded) {
                        setOpenUsageCode(null);
                        return;
                      }
                      setOpenUsageCode(machine.machineCode);
                      if (!usagePages[machine.machineCode]?.items.length) {
                        void loadUsagePage(machine.machineCode, 0);
                      }
                    }}
                    disableGutters
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: alpha('#ff8ba7', 0.35),
                      borderRadius: '12px !important',
                      overflow: 'visible',
                      bgcolor: '#fff',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        position: open ? 'sticky' : 'relative',
                        top: open ? usageFiltersHeight : 0,
                        zIndex: 2,
                        bgcolor: '#fff8fb',
                        borderRadius: '12px',
                        borderBottom: open ? '1px solid' : 'none',
                        borderColor: alpha('#ff8ba7', 0.28),
                        '& .MuiAccordionSummary-content': { minWidth: 0, my: 1 },
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1, pr: 0.5 }}>
                        <Typography fontWeight={800} noWrap>
                          {machine.machineName || machine.machineCode}
                        </Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25, minWidth: 0 }}>
                          <Typography noWrap sx={{ color: '#ff8ba7', fontWeight: 700, minWidth: 0 }}>
                            {machine.machineCode}
                          </Typography>
                          <Chip
                            size="small"
                            label={formatMessage(b.usageShowing, { n: machine.total })}
                            sx={{ flexShrink: 0, height: 22, maxWidth: '100%' }}
                          />
                        </Stack>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {page?.loading && items.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={22} />
                        </Box>
                      ) : null}
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 1.25,
                        }}
                      >
                        {items.map((item, index) => {
                          const urls = (item.resultUrls?.length ? item.resultUrls : [item.resultUrl]).filter(Boolean);
                          const multi = urls.length >= 2;
                          return (
                            <Box
                              key={`${machine.machineCode}-${item.createdAt || index}-${index}`}
                              sx={{
                                gridColumn: multi ? '1 / -1' : 'auto',
                                minWidth: 0,
                                p: 0.75,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: alpha('#ff8ba7', 0.7),
                                bgcolor: alpha('#ff8ba7', 0.05),
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: multi ? '1fr 1fr' : '1fr',
                                  gap: 0.75,
                                }}
                              >
                                {urls.map((url) => (
                                  <Box
                                    key={url}
                                    component="img"
                                    src={url}
                                    alt=""
                                    sx={{
                                      width: '100%',
                                      aspectRatio: '1',
                                      objectFit: 'cover',
                                      borderRadius: 1.5,
                                      bgcolor: alpha('#ff8ba7', 0.08),
                                    }}
                                  />
                                ))}
                              </Box>
                              {(usageName(item.themeLabels, item.themeName, lang) || usageName(item.presetLabels, item.presetName, lang)) ? (
                                <Stack spacing={0.15} sx={{ mt: 0.75 }}>
                                  {usageName(item.themeLabels, item.themeName, lang) ? (
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#9d174d', lineHeight: 1.3 }}>
                                      {copy.usageTheme}: {usageName(item.themeLabels, item.themeName, lang)}
                                    </Typography>
                                  ) : null}
                                  {usageName(item.presetLabels, item.presetName, lang) ? (
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', lineHeight: 1.3 }}>
                                      {copy.usageBackground}: {usageName(item.presetLabels, item.presetName, lang)}
                                    </Typography>
                                  ) : null}
                                </Stack>
                              ) : null}
                              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                                <Chip size="small" label={qualityLabel(item.quality)} sx={{ height: 22 }} />
                                {item.imageSize ? <Chip size="small" variant="outlined" label={item.imageSize} sx={{ height: 22 }} /> : null}
                                {product ? <Chip size="small" label={product.toUpperCase()} sx={{ height: 22 }} /> : null}
                              </Stack>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.35 }}>
                                {item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                      {items.length < machine.total && (
                        <Button size="small" disabled={page?.loading} onClick={() => void loadUsagePage(machine.machineCode, items.length)} sx={{ mt: 1.25 }}>
                          {page?.loading ? b.loading : b.usageLoadMore}
                        </Button>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUsage} sx={{ color: '#bf5f82', fontWeight: 700 }}>{copy.cancel}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirm)} onClose={() => !saving && setConfirm(null)}>
        <DialogTitle>
          {confirm?.kind === 'theme'
            ? copy.confirmDeleteTheme
            : confirm?.kind === 'tag'
              ? ({ vi: 'Xóa tag này?', ko: '이 태그를 삭제할까요?', en: 'Delete this tag?', zh: '删除此标签？' } as const)[lang]
              : copy.confirmDeleteBackground}
        </DialogTitle>
        <DialogContent>
          <Typography>{confirm?.name}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={saving}>{copy.cancel}</Button>
          <Button color="error" variant="contained" onClick={() => void runDelete()} disabled={saving}>{copy.delete}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserAiThemes;
